import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  Trash2,
  Folder,
  Loader2,
  RefreshCw,
  Settings,
  ChevronRight,
  AlertCircle,
  Clock
} from "lucide-react";
import ExportButton from "./ExportButton";

const ContactGroupTableItem = ({
  group,
  onView,
  onDownload,
  onDelete,
  onAddToGroup,
  onRefreshGroup,
}) => {
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const timeoutRef = useRef(null);

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getContactCountColor = (count) => {
    if (count === 0) return "text-blue-500";
    if (count < 100) return "text-blue-400";
    if (count < 1000) return "text-blue-600";
    return "text-purple-600";
  };

  const getContactCountBg = (count) => {
    if (count === 0) return "bg-blue-100";
    if (count < 100) return "bg-blue-100";
    if (count < 1000) return "bg-blue-100";
    return "bg-purple-100";
  };

  // Check if group is still being processed
  const isProcessing = !group.uploaded_at;
  const contactCount = group.number_of_contacts || 0;

  // Get row styling based on contact count
  const getRowStyling = (count) => {
    if (isProcessing) {
      return {
        borderLeft: showTimeoutWarning 
          ? "border-l-4 border-red-400" 
          : "border-l-4 border-orange-400",
        iconBg: showTimeoutWarning 
          ? "from-red-500 to-orange-600" 
          : "from-orange-500 to-amber-600",
        hoverBg: showTimeoutWarning 
          ? "hover:bg-red-50/50" 
          : "hover:bg-orange-50/50",
      };
    }
    if (count === 0) {
      return {
        borderLeft: "border-l-4 border-blue-400",
        iconBg: "from-blue-400 to-blue-500",
        hoverBg: "hover:bg-blue-50/50",
      };
    }
    if (count < 100) {
      return {
        borderLeft: "border-l-4 border-blue-400",
        iconBg: "from-blue-400 to-blue-600",
        hoverBg: "hover:bg-blue-50/50",
      };
    }
    if (count < 1000) {
      return {
        borderLeft: "border-l-4 border-red-400",
        iconBg: "from-red-500 to-red-600",
        hoverBg: "hover:bg-red-50/50",
      };
    }
    return {
      borderLeft: "border-l-4 border-purple-400",
      iconBg: "from-purple-500 to-violet-600",
      hoverBg: "hover:bg-purple-50/50",
    };
  };

  // Timer for processing timeout
  useEffect(() => {
    if (isProcessing) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout for 5 seconds
      timeoutRef.current = setTimeout(() => {
        setShowTimeoutWarning(true);
      }, 5000); // 5 seconds

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    } else {
      // Not processing, reset the warning
      setShowTimeoutWarning(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [isProcessing, group.uploaded_at]);

  const rowStyle = getRowStyling(contactCount);

  return (
    <motion.tr
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={`border-b border-gray-100 ${rowStyle.borderLeft} ${rowStyle.hoverBg} transition-colors duration-200`}
    >
      {/* Group Name & Icon - 35% width */}
      <td className="py-4 px-4 w-[35%]">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 bg-gradient-to-br ${rowStyle.iconBg} rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}
          >
            <Folder className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className="text-sm font-semibold text-gray-900 truncate"
              title={group.group_name}
            >
              {group.group_name}
            </h3>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Calendar className="w-3 h-3 flex-shrink-0" />
              <span>{formatDate(group.created_at)}</span>
            </div>
          </div>
        </div>
      </td>

      {/* Contact Count - 20% width */}
      <td className="py-4 px-4 w-[20%]">
        {isProcessing ? (
          showTimeoutWarning ? (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-600">
              Processing
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-4 h-4"
              >
                <Loader2 className="w-4 h-4 text-orange-600" />
              </motion.div>
              <span className="text-sm font-medium text-orange-600">
                Processing...
              </span>
            </div>
          )
        ) : (
          <div
            className={`inline-flex items-center justify-center px-3 py-1.5 rounded-full ${getContactCountBg(
              contactCount
            )} border border-white/50 min-w-[100px]`}
          >
            <span
              className={`text-sm font-semibold ${getContactCountColor(
                contactCount
              )}`}
            >
              {contactCount.toLocaleString()}
            </span>
          </div>
        )}
      </td>

      {/* Status - 15% width */}
      <td className="py-4 px-4 w-[15%]">
        <div className="flex items-center gap-2">
          <div
            className={`w-2.5 h-2.5 rounded-full border border-white ${
              showTimeoutWarning
                ? "bg-red-500"
                : isProcessing
                ? "bg-yellow-500"
                : contactCount > 0
                ? "bg-green-500"
                : "bg-gray-400"
            }`}
          />
          <span className="text-sm text-gray-600">
            {showTimeoutWarning
              ? "Timeout"
              : isProcessing
              ? "Processing"
              : contactCount > 0
              ? "Active"
              : "Empty"}
          </span>
        </div>
      </td>

      {/* Actions - 30% width */}
      <td className="py-4 px-4 w-[30%]">
        <div className="flex items-center justify-start gap-2">
          {/* Manage Group Button */}
          {!isProcessing && !showTimeoutWarning && (
            <motion.button
              onClick={() => onAddToGroup(group)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:bg-gray-50 hover:shadow"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Manage Group"
            >
              <Settings className="w-3.5 h-3.5" />
              <span>Manage</span>
            </motion.button>
          )}

          {/* Export or Refresh/Retry Button */}
          {isProcessing ? (
            <motion.button
              onClick={() => {
                if (onRefreshGroup) {
                  onRefreshGroup(group);
                  // Reset the warning when manually refreshing
                  setShowTimeoutWarning(false);
                }
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg border shadow-sm transition-all duration-200 ${
                showTimeoutWarning
                  ? "bg-gradient-to-r from-red-500 to-orange-600 border-red-500 hover:from-red-600 hover:to-orange-700"
                  : "bg-gradient-to-r from-orange-500 to-amber-600 border-orange-500 hover:from-orange-600 hover:to-amber-700"
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title={showTimeoutWarning ? "Retry updating contacts" : "Check if processing is complete"}
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>{showTimeoutWarning ? "Retry" : "Refresh"}</span>
            </motion.button>
          ) : (
            <div className="inline-flex">
              <ExportButton
                groupSlug={group.contact_group_slug}
                groupName={group.group_name}
                contactCount={contactCount}
                variant="compact"
                disabled={contactCount === 0}
              />
            </div>
          )}

          {/* Delete Button */}
          <motion.button
            onClick={() => onDelete(group)}
            className="p-1.5 text-gray-400 rounded-lg transition-all duration-200 hover:text-red-600 hover:bg-red-50"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title="Delete Group"
          >
            <Trash2 className="w-4 h-4" />
          </motion.button>

          {/* View Details Button */}
          {onView && (
            // <motion.button
            //   onClick={() => onView(group)}
            //   className="p-1.5 text-gray-400 rounded-lg transition-all duration-200 hover:text-blue-600 hover:bg-blue-50"
            //   whileHover={{ scale: 1.1 }}
            //   whileTap={{ scale: 0.9 }}
            //   title="View Details"
            // >
            //   <ChevronRight className="w-4 h-4" />
            // </motion.button>
            <></>
          )}
        </div>
      </td>
    </motion.tr>
  );
};

export default ContactGroupTableItem;